import { useEffect, useRef, useState, useCallback } from 'react';
import { loadDashboardFilters, saveDashboardFilters} from 'service/service';
import {DashboardFilterConfig} from './constants/dashboardConfig';
import { filterCache as defaultFilterCache } from '../../src/components/PipelineRunStatus/utils/filterCache';

interface UseDashboardFilterPersistenceOptions<T> {
  config: DashboardFilterConfig; /** Dashboard identification configuration (ID, name, page name) */
  currentFilters: T; /** Current filter state that will be auto-saved when changed */
  onFiltersLoaded: (filters: T) => void;/** Callback function triggered when saved filters are loaded from database */
  filterCache?: typeof defaultFilterCache;
  debounceDelay?: number;
  enabled?: boolean;
}

/**
 * Custom hook for persisting dashboard filters across sessions
 * 
 * This hook provides automatic save/load functionality for dashboard filters:
 * - Automatically loads saved filters when component mounts
 * - Auto-saves filters when they change 
 * - Provides manual save and reset methods
 * - Prevents race conditions during initial load
 * 
 * @template T - The type of the filter state object
 * @param options - Configuration options for the hook
 * @returns Object containing loading states and control functions
 * 
 * @example
 * ```typescript
 * const { isLoading, isSaving, hasSavedFilters, manualSave, resetToSaved } = 
 *   useDashboardFilterPersistence({
 *     config: {
 *       dashboardId: 'pipeline-run-status',
 *       dashboardName: 'Pipeline Run Status',
 *       pageName: 'Pipeline Run Status Dashboard'
 *     },
 *     currentFilters: appliedFilters,
 *     onFiltersLoaded: (savedFilters) => setAppliedFilters(savedFilters),
 *     debounceDelay: 1000,
 *     enabled: true
 *   });
 * ```
 */
export const useDashboardFilterPersistence = <T>(
  options: UseDashboardFilterPersistenceOptions<T>
) => {
  const {
    config,
    currentFilters,
    onFiltersLoaded,
    filterCache = defaultFilterCache,
    debounceDelay = 1000,
    enabled = true
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedFilters, setHasSavedFilters] = useState(false);
  const hasLoadedSavedFiltersRef = useRef(false);
  const [savedFilters, setSavedFilters] = useState<T | null>(null); 

   /*
   * Loads saved filters from the database when the component mounts
   * This runs only once per dashboard instance and ensures user preferences
   * are restored across sessions.
   */

  useEffect(() => {
    const loadFilters = async () => {
      setIsLoading(true);
      try {
        const savedData = await loadDashboardFilters(config);
        if (savedData?.filters) {
          setHasSavedFilters(true);
          setSavedFilters(savedData.filters)
          hasLoadedSavedFiltersRef.current = true;
          onFiltersLoaded(savedData.filters);
          console.log(`✅ Loaded saved filters for ${config.dashboardId}`);
        } else {
          setHasSavedFilters(false);
          setSavedFilters(null);
          hasLoadedSavedFiltersRef.current = true; 
          onFiltersLoaded(null as unknown as T);
          console.log(`ℹ️ No saved filters found for ${config.dashboardId}`);
        }
      } catch (error) {
        console.error(`❌ Error loading saved filters for ${config.dashboardId}:`, error);
        hasLoadedSavedFiltersRef.current = true; 
      } finally {
        setIsLoading(false);
      }
    };
    
    if (enabled) {
      loadFilters();
    } else {
      setIsLoading(false);
      hasLoadedSavedFiltersRef.current = true;
    }
  }, [config.dashboardId, enabled]);

   /**
   * Manually saves the current filters to the database
   */

   const manualSave = useCallback(async (filtersToSave?: T) => {
    if (!enabled) return { success: false };
      const filters = filtersToSave !== undefined ? filtersToSave : currentFilters;
    setIsSaving(true);
    try {
      await saveDashboardFilters(config, filters);
      setHasSavedFilters(true);
      setSavedFilters(filters);
      console.log(`💾 Manually saved filters for ${config.dashboardId}`, filters);
      return { success: true };
    } catch (error) {
      console.error(`❌ Error manually saving filters for ${config.dashboardId}:`, error);
      return { success: false, error };
    } finally {
      setIsSaving(false);
    }
  }, [currentFilters, config, enabled]);


  
  /**
   * Use this when you want to provide a "Reset to Default" button,
   * allowing users to discard unsaved changes and restore Default saved preferences.
   */


const resetToSaved = useCallback(async () => {
  if (!enabled) return { success: false };
  
  try {
    const savedData = await loadDashboardFilters(config);
    if (savedData?.filters) {
      filterCache.saveAppliedFilters(savedData.filters);
      
      filterCache.clearAll();
      
      onFiltersLoaded(savedData.filters);
      
      console.log(`🔄 Reset to saved filters for ${config.dashboardId}`);
      return { success: true, filters: savedData.filters };
    }
    return { success: false, message: 'No saved filters found' };
  } catch (error) {
    console.error(`❌ Error resetting to saved filters for ${config.dashboardId}:`, error);
    return { success: false, error };
  }
}, [config, enabled, filterCache, onFiltersLoaded]);

  return {
    isLoading,
    isSaving,
    hasSavedFilters,
    savedFilters,
    manualSave,
    resetToSaved,
  };
};